import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CSpinner,
  CFormInput,
  CFormSelect,
  CRow,
  CCol,
  CButton,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilWarning, cilCheckCircle, cilSearch, cilFilterX } from '@coreui/icons';
import i18n from '../../../i18n';

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    const d = new Date(dateString);
    return d.toLocaleString();
  } catch (e) {
    return dateString;
  }
};

const getSeverityBadge = (type) => {
  switch (type?.toLowerCase()) {
    case 'critical':
    case 'danger':
    case 'power_off':
    case 'ai_frozen':
      return <CBadge color="danger">{i18n.t('alerts_history.severity_critical', 'Критический')}</CBadge>;
    case 'warning':
    case 'battery':
    case 'temp':
    case 'dark':
      return <CBadge color="warning">{i18n.t('alerts_history.severity_warning', 'Предупреждение')}</CBadge>;
    default:
      return <CBadge color="info">{i18n.t('alerts_history.severity_info', 'Информация')}</CBadge>;
  }
};

const AlertsHistory = ({ loading, alerts }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const filteredAlerts = (alerts || []).filter((alert) => {
    const matchesSearch =
      (alert.device_name && alert.device_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (alert.description && alert.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (alert.message && alert.message.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = typeFilter === 'all' || alert.alert_type === typeFilter;

    return matchesSearch && matchesType;
  });

  const resetFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <CSpinner color="primary" />
        <span className="ms-2">{i18n.t('alerts_history.loading', 'Загрузка истории инцидентов...')}</span>
      </div>
    );
  }

  return (
    <div>
      <CRow className="mb-3 g-2 align-items-center">
        <CCol md={4} sm={12}>
          <div className="input-group">
            <span className="input-group-text bg-white">
              <CIcon icon={cilSearch} size="sm" />
            </span>
            <CFormInput
              placeholder={i18n.t('alerts_history.search_placeholder', 'Поиск по технике или описанию...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CCol>
        <CCol md={3} sm={6}>
          <CFormSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label={i18n.t('alerts_history.filter_by_type', 'Фильтр по типу инцидента')}
          >
            <option value="all">{i18n.t('alerts_history.all_types', 'Все типы инцидентов')}</option>
            <option value="battery">{i18n.t('alerts_history.type_battery', 'Батарея')}</option>
            <option value="temp">{i18n.t('alerts_history.type_temp', 'Температура')}</option>
            <option value="power_off">{i18n.t('alerts_history.type_power_off', 'Отключение питания')}</option>
            <option value="ai_frozen">{i18n.t('alerts_history.type_ai_frozen', 'Сбой нейросети')}</option>
            <option value="idle_truck">{i18n.t('alerts_history.type_idle', 'Простой техники')}</option>
          </CFormSelect>
        </CCol>
        <CCol md={2} sm={6}>
          <CButton color="light" variant="outline" onClick={resetFilters} className="w-100">
            <CIcon icon={cilFilterX} className="me-1" />
            {i18n.t('alerts_history.reset', 'Сбросить')}
          </CButton>
        </CCol>
      </CRow>

      <div className="table-responsive bg-white rounded border">
        <CTable hover align="middle" className="mb-0">
          <CTableHead color="light">
            <CTableRow>
              <CTableHeaderCell scope="col" style={{ width: '60px' }}>#</CTableHeaderCell>
              <CTableHeaderCell scope="col">{i18n.t('alerts_history.time', 'Время')}</CTableHeaderCell>
              <CTableHeaderCell scope="col">{i18n.t('alerts_history.device', 'Оборудование / Техника')}</CTableHeaderCell>
              <CTableHeaderCell scope="col">{i18n.t('alerts_history.type', 'Тип инцидента')}</CTableHeaderCell>
              <CTableHeaderCell scope="col">{i18n.t('alerts_history.description', 'Описание')}</CTableHeaderCell>
              <CTableHeaderCell scope="col" className="text-center">{i18n.t('alerts_history.status', 'Статус')}</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {filteredAlerts.length > 0 ? (
              filteredAlerts.map((item, index) => (
                <CTableRow key={item.id || index}>
                  <CTableDataCell className="text-muted small">{index + 1}</CTableDataCell>
                  <CTableDataCell className="small font-monospace">
                    {formatDateTime(item.created_at || item.timestamp)}
                  </CTableDataCell>
                  <CTableDataCell className="fw-semibold">
                    {item.device_name || item.device || '-'}
                  </CTableDataCell>
                  <CTableDataCell>
                    {getSeverityBadge(item.alert_type || item.level)}
                  </CTableDataCell>
                  <CTableDataCell className="small">
                    {item.description || item.message || '-'}
                  </CTableDataCell>
                  <CTableDataCell className="text-center">
                    {item.resolved ? (
                      <span className="text-success small d-flex align-items-center justify-content-center">
                        <CIcon icon={cilCheckCircle} className="me-1" />
                        {i18n.t('alerts_history.status_resolved', 'Решено')}
                      </span>
                    ) : (
                      <span className="text-danger small d-flex align-items-center justify-content-center">
                        <CIcon icon={cilWarning} className="me-1" />
                        {i18n.t('alerts_history.status_active', 'Активен')}
                      </span>
                    )}
                  </CTableDataCell>
                </CTableRow>
              ))
            ) : (
              <CTableRow>
                <CTableDataCell colSpan={6} className="text-center py-4 text-muted">
                  {i18n.t('alerts_history.no_data', 'Инциденты не найдены')}
                </CTableDataCell>
              </CTableRow>
            )}
          </CTableBody>
        </CTable>
      </div>
    </div>
  );
};

AlertsHistory.propTypes = {
  loading: PropTypes.bool,
  alerts: PropTypes.arrayOf(PropTypes.object),
};

AlertsHistory.defaultProps = {
  loading: false,
  alerts: [],
};

export default AlertsHistory;